/**
 * Analytics Dashboard Component
 * 
 * Displays analytics for all content types:
 * - Videos (Peeks)
 * - Blogs (Articles)  
 * - Music
 * - Audio
 * - Ads
 */
import React, { useEffect, useState } from "react";
import styles from "./Analytics.module.scss";
import {
  getAnalyticsDashboard,
  getContentAnalytics,
} from "../../services/viewTracking.service";
import { getAds } from "../../redux/slices/ads.slice";
import { useDispatch } from "react-redux";
import Loader from "../../components/Loader";
import moment from "moment";
import toast from "react-hot-toast";
import ReactPaginate from "react-paginate";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const AnalyticsDashboard = () => {
  const dispatch = useDispatch();
  const [dashboardData, setDashboardData] = useState(null);
  const [adsData, setAdsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(null); // null = overview, 'video', 'blog', 'music', 'audio', 'ad'
  const [contentAnalytics, setContentAnalytics] = useState([]);
  const [contentLoading, setContentLoading] = useState(false);
  const [, setCurrentPage] = useState(0);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    totalPages: 1,
    totalCount: 0,
  });

  const token = localStorage.getItem("j-token"); // Admin auth token

  useEffect(() => {
    fetchDashboardAnalytics();
    fetchAdsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardAnalytics = async () => {
    try {
      setLoading(true);
      console.log("📊 Fetching dashboard analytics...");
      console.log("Token:", token ? "✅ Present" : "❌ Missing");
      
      const response = await getAnalyticsDashboard(token);
      console.log("📊 Response received:", response);
      
      if (response && (response.status === 200 || response.status === "success")) {
        console.log("✅ Analytics data loaded successfully");
        console.log("Summary:", response.data?.summary);
        setDashboardData(response.data);
      } else {
        console.error("❌ Invalid response status:", response?.status);
        toast.error("Failed to fetch analytics: " + (response?.message || "Invalid response"));
      }
    } catch (error) {
      console.error("❌ Error fetching analytics:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      toast.error("Failed to load analytics data: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdsData = async (page = 1) => {
    try {
      dispatch(
        getAds({
          payload: { page },
          callback: (data) => {
            if (data?.status === 200) {
              const ads = data?.data || [];
              // Calculate ads analytics
              const totalImpressions = ads.reduce((sum, ad) => sum + (ad.impressions || 0), 0);
              const totalClicks = ads.reduce((sum, ad) => sum + (ad.clicks || 0), 0);
              const maxImpressions = Math.max(...ads.map(ad => ad.impressions || 0), 0);
              const maxClicks = Math.max(...ads.map(ad => ad.clicks || 0), 0);
              
              // Sort ads by impressions for top ads
              const sortedAds = [...ads].sort((a, b) => (b.impressions || 0) - (a.impressions || 0));
              const topAds = sortedAds.slice(0, 5).map(ad => ({
                id: ad.id,
                link: ad.link,
                impressions: ad.impressions || 0,
                clicks: ad.clicks || 0,
                imageUrl: ad.imageUrl,
                videoUrl: ad.videoUrl,
              }));

              setAdsData({
                summary: {
                  totalCount: data?.totalCount || ads.length,
                  totalImpressions,
                  totalClicks,
                  maxImpressions,
                  maxClicks,
                },
                topAds,
                allAds: ads,
                pagination: {
                  page: data?.page || 1,
                  pageSize: data?.pageSize || 20,
                  totalPages: data?.totalPages || 1,
                  totalCount: data?.totalCount || ads.length,
                },
              });
            }
          },
        })
      );
    } catch (error) {
      console.error("Failed to fetch ads data:", error);
    }
  };

  const fetchContentAnalytics = async (type, page = 1) => {
    try {
      setContentLoading(true);
      
      // Handle ads separately since they come from different API
      if (type === 'ad') {
        if (adsData && adsData.allAds) {
          const sortedAds = [...adsData.allAds].sort((a, b) => 
            new Date(b.createdAt) - new Date(a.createdAt)
          );
          setContentAnalytics(sortedAds);
          setPagination(adsData.pagination);
        }
        setContentLoading(false);
        return;
      }
      
      const response = await getContentAnalytics(type, page, 20, token);
      if (response.status === 200 || response.status === "success") {
        setContentAnalytics(response.data);
        setPagination(response.pagination || {
          page: page,
          pageSize: 20,
          totalPages: Math.ceil(response.data.length / 20),
          totalCount: response.data.length,
        });
      } else {
        toast.error("Failed to fetch content analytics");
      }
    } catch (error) {
      console.error(`Error fetching ${type} analytics:`, error);
      toast.error("Failed to load content data");
    } finally {
      setContentLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(0);
    if (tab) {
      fetchContentAnalytics(tab, 1);
    }
  };

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
    if (activeTab) {
      fetchContentAnalytics(activeTab, selected + 1);
    }
  };

  if (loading && !dashboardData) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <Loader />
      </div>
    );
  }

  return (
        <div className={styles.analyticsContainer}>
      <h1 className={styles.title}>Analytics Dashboard</h1>

      {/* Overview Section */}
      {!activeTab && dashboardData && (
        <div className={styles.overviewSection}>
          {/* Content Analytics Header */}
          <div className={styles.sectionHeader}>
            <h2 className={styles.mainSectionTitle}>📊 Content Performance Analytics</h2>
            <p className={styles.sectionDescription}>Track and analyze your content engagement and growth</p>
          </div>
          
          {/* Summary Cards */}
          <div className={styles.summaryCards}>
            {[
              { type: "videos", label: "Peeks", color: "#3eb489" },
              { type: "blogs", label: "Articles", color: "#ff3c00" },
              { type: "music", label: "Music", color: "#8b5cf6" },
              { type: "audio", label: "Audio", color: "#06b6d4" },
            ].map((item) => {
              const data = dashboardData.summary[item.type];
              // Skip if data doesn't exist
              if (!data) return null;
              // Convert plural to singular for API call
              const singularType = item.type === "videos" ? "video" : item.type === "blogs" ? "blog" : item.type;
              return (
                <div
                  key={item.type}
                  className={styles.card}
                  onClick={() => handleTabChange(singularType)}
                  style={{ cursor: "pointer", borderLeft: `5px solid ${item.color}` }}
                >
                  <div className={styles.cardTitle}>{item.label}</div>
                  <div className={styles.cardStat}>
                    <div className={styles.statItem}>
                      <span className={styles.label}>Total Count:</span>
                      <span className={styles.value}>{data.totalCount}</span>
                    </div>
                    <div className={styles.statItem}>
                      <span className={styles.label}>Total Views:</span>
                      <span className={styles.value}>{data.totalViews}</span>
                    </div>
                    <div className={styles.statItem}>
                      <span className={styles.label}>Max Views:</span>
                      <span className={styles.value}>{data.maxViews}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Charts Section */}
          <div className={styles.chartsSection}>
            <div className={styles.chartCard}>
              <h3 className={styles.chartTitle}>Content Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={[
                      { name: "Peeks", value: dashboardData.summary.videos.totalCount, color: "#3eb489" },
                      { name: "Articles", value: dashboardData.summary.blogs.totalCount, color: "#ff3c00" },
                      { name: "Music", value: dashboardData.summary.music.totalCount, color: "#8b5cf6" },
                      { name: "Audio", value: dashboardData.summary.audio.totalCount, color: "#06b6d4" },
                    ].filter(item => item.value > 0)} // Filter out items with 0 count
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {[
                      { name: "Peeks", value: dashboardData.summary.videos.totalCount, color: "#3eb489" },
                      { name: "Articles", value: dashboardData.summary.blogs.totalCount, color: "#ff3c00" },
                      { name: "Music", value: dashboardData.summary.music.totalCount, color: "#8b5cf6" },
                      { name: "Audio", value: dashboardData.summary.audio.totalCount, color: "#06b6d4" },
                    ].map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className={styles.chartCard}>
              <h3 className={styles.chartTitle}>Total Views by Content Type</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={[
                    { name: "Peeks", value: dashboardData.summary.videos.totalViews, color: "#3eb489" },
                    { name: "Articles", value: dashboardData.summary.blogs.totalViews, color: "#ff3c00" },
                    { name: "Music", value: dashboardData.summary.music.totalViews, color: "#8b5cf6" },
                    { name: "Audio", value: dashboardData.summary.audio.totalViews, color: "#06b6d4" },
                  ]}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" fill="#8884d8">
                    {[
                      { name: "Peeks", value: dashboardData.summary.videos.totalViews, color: "#3eb489" },
                      { name: "Articles", value: dashboardData.summary.blogs.totalViews, color: "#ff3c00" },
                      { name: "Music", value: dashboardData.summary.music.totalViews, color: "#8b5cf6" },
                      { name: "Audio", value: dashboardData.summary.audio.totalViews, color: "#06b6d4" },
                    ].map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top Content Section */}
          <div className={styles.topContentSection}>
            <h2 className={styles.sectionTitle}>Top Content</h2>
            <div className={styles.topContentGrid}>
              {["topVideos", "topBlogs", "topMusic", "topAudio"].map((contentType) => {
                const labelMap = {
                  topVideos: "Peeks",
                  topBlogs: "Articles",
                  topMusic: "Music",
                  topAudio: "Audio"
                };
                const contentLabel = labelMap[contentType];
                const items = (dashboardData.topContent[contentType] || [])
                  .sort((a, b) => ((b.views || 0) - (a.views || 0)))
                  .slice(0, 5); // Show only top 5 items
                return (
                  <div key={contentType} className={styles.topContentCard}>
                    <h3 className={styles.contentTypeTitle}>Top {contentLabel} ({items.length})</h3>
                    <div className={styles.contentList}>
                      {items.length > 0 ? (
                        items.map((item, idx) => {
                          // Get the correct image field based on content type
                          let thumbnailUrl = null;
                          if (contentType === 'topVideos') {
                            thumbnailUrl = item.thumbnailUrl;
                          } else if (contentType === 'topBlogs') {
                            thumbnailUrl = item.sharingImage;
                          } else if (contentType === 'topMusic' || contentType === 'topAudio') {
                            thumbnailUrl = item.thumbnailUrl;
                          }
                          
                          // Fallback to other possible image fields
                          if (!thumbnailUrl) {
                            thumbnailUrl = item.thumbnail || item.image || item.sharpImages?.[0] || null;
                          }
                          
                          return (
                          <div key={idx} className={styles.contentItem}>
                            <span className={styles.rank}>{idx + 1}</span>
                            {thumbnailUrl && (
                              <img 
                                src={thumbnailUrl} 
                                alt={item.title}
                                style={{
                                  width: '50px',
                                  height: '50px',
                                  objectFit: 'cover',
                                  borderRadius: '6px',
                                  marginRight: '12px'
                                }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            )}
                            <div className={styles.contentInfo}>
                              <p className={styles.contentTitle}>{item.title || 'Untitled'}</p>
                              <p className={styles.contentViews}>{item.views || 0} views</p>
                            </div>
                          </div>
                          );
                        })
                      ) : (
                        <p className={styles.noData}>No data available</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Ads Analytics Section */}
          {adsData && adsData.allAds && adsData.allAds.length > 0 && (
            <div className={styles.adsAnalyticsSection}>
              {/* Ads Analytics Header */}
              <div className={styles.adsHeader}>
                <h2 className={styles.mainSectionTitle}>🎯 Advertising Performance Dashboard</h2>
                <p className={styles.sectionDescription}>Monitor your ad campaigns with real-time metrics and insights</p>
              </div>
              
              {/* Ads KPI Cards */}
              <h3 className={styles.subSectionTitle}>Key Performance Indicators</h3>
              <div className={styles.adsKpiCards}>
                <div className={styles.kpiCard} style={{ borderLeft: '4px solid #f59e0b' }}>
                  <div className={styles.kpiLabel}>Total Ads</div>
                  <div className={styles.kpiValue}>{adsData.summary.totalCount}</div>
                </div>
                <div className={styles.kpiCard} style={{ borderLeft: '4px solid #3b82f6' }}>
                  <div className={styles.kpiLabel}>Total Impressions</div>
                  <div className={styles.kpiValue}>{adsData.summary.totalImpressions.toLocaleString()}</div>
                </div>
                <div className={styles.kpiCard} style={{ borderLeft: '4px solid #10b981' }}>
                  <div className={styles.kpiLabel}>Total Clicks</div>
                  <div className={styles.kpiValue}>{adsData.summary.totalClicks.toLocaleString()}</div>
                </div>
                <div className={styles.kpiCard} style={{ borderLeft: '4px solid #8b5cf6' }}>
                  <div className={styles.kpiLabel}>Avg CTR</div>
                  <div className={styles.kpiValue}>
                    {adsData.summary.totalImpressions > 0 
                      ? ((adsData.summary.totalClicks / adsData.summary.totalImpressions) * 100).toFixed(2) 
                      : 0}%
                  </div>
                </div>
              </div>

              {/* Ads Charts */}
              <h3 className={styles.subSectionTitle}>📈 Performance Insights & Trends</h3>
              <div className={styles.adsChartsSection}>
                {/* Impressions vs Clicks Chart */}
                <div className={styles.chartCard}>
                  <h3 className={styles.chartTitle}>👁️ Impressions vs 👆 Clicks</h3>
                  <p className={styles.chartSubtitle}>Compare visibility and engagement metrics</p>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={adsData.topAds.map((ad, index) => ({
                        name: `Ad ${index + 1}`,
                        Impressions: ad.impressions,
                        Clicks: ad.clicks,
                        link: ad.link,
                        imageUrl: ad.imageUrl || ad.videoUrl,
                      }))}
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="name" 
                        angle={0} 
                        textAnchor="middle" 
                        height={50}
                      />
                      <YAxis />
                      <Tooltip 
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <div style={{ backgroundColor: 'white', padding: '10px', border: '1px solid #ccc', borderRadius: '4px', maxWidth: '300px' }}>
                                <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>{payload[0].payload.name}</p>
                                {payload[0].payload.imageUrl && (
                                  <img 
                                    src={payload[0].payload.imageUrl} 
                                    alt="Ad" 
                                    style={{ width: '100%', height: 'auto', maxHeight: '150px', objectFit: 'cover', borderRadius: '4px', marginBottom: '8px' }}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                )}
                                <p style={{ margin: '0 0 5px 0', fontSize: '12px', color: '#666', wordBreak: 'break-all' }}>{payload[0].payload.link}</p>
                                {payload.map((entry, index) => (
                                  <p key={index} style={{ margin: '3px 0', color: entry.color }}>
                                    {entry.name}: {entry.value.toLocaleString()}
                                  </p>
                                ))}
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Legend />
                      <Bar dataKey="Impressions" fill="#3b82f6" />
                      <Bar dataKey="Clicks" fill="#10b981" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* CTR by Ad Chart */}
                <div className={styles.chartCard}>
                  <h3 className={styles.chartTitle}>🎯 Click-Through Rate Performance</h3>
                  <p className={styles.chartSubtitle}>Higher CTR indicates better ad engagement</p>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={adsData.topAds.map((ad, index) => ({
                        name: `Ad ${index + 1}`,
                        CTR: ad.impressions > 0 ? ((ad.clicks / ad.impressions) * 100).toFixed(2) : 0,
                        link: ad.link,
                        imageUrl: ad.imageUrl || ad.videoUrl,
                      }))}
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="name" 
                        angle={0} 
                        textAnchor="middle" 
                        height={50}
                      />
                      <YAxis label={{ value: 'CTR (%)', angle: -90, position: 'insideLeft' }} />
                      <Tooltip 
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <div style={{ backgroundColor: 'white', padding: '10px', border: '1px solid #ccc', borderRadius: '4px', maxWidth: '300px' }}>
                                <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>{payload[0].payload.name}</p>
                                {payload[0].payload.imageUrl && (
                                  <img 
                                    src={payload[0].payload.imageUrl} 
                                    alt="Ad" 
                                    style={{ width: '100%', height: 'auto', maxHeight: '150px', objectFit: 'cover', borderRadius: '4px', marginBottom: '8px' }}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                )}
                                <p style={{ margin: '0 0 5px 0', fontSize: '12px', color: '#666', wordBreak: 'break-all' }}>{payload[0].payload.link}</p>
                                <p style={{ margin: '3px 0', color: payload[0].color }}>
                                  CTR: {payload[0].value}%
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Legend />
                      <Bar dataKey="CTR" fill="#8b5cf6">
                        {adsData.topAds.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={
                              entry.impressions > 0 && (entry.clicks / entry.impressions) > 0.15 
                                ? '#10b981' 
                                : (entry.clicks / entry.impressions) > 0.08 
                                ? '#f59e0b' 
                                : '#ef4444'
                            } 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Impressions Distribution Pie Chart */}
                <div className={styles.chartCard}>
                  <h3 className={styles.chartTitle}>👁️ Visibility Distribution</h3>
                  <p className={styles.chartSubtitle}>How impressions are shared across your ads</p>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={adsData.topAds.map((ad, idx) => ({
                          name: `Ad ${idx + 1}`,
                          value: ad.impressions,
                          link: ad.link,
                          imageUrl: ad.imageUrl || ad.videoUrl,
                        }))}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {adsData.topAds.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={['#f59e0b', '#3b82f6', '#10b981', '#8b5cf6', '#ef4444'][index % 5]} 
                          />
                        ))}
                      </Pie>
                      <Tooltip 
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <div style={{ backgroundColor: 'white', padding: '10px', border: '1px solid #ccc', borderRadius: '4px', maxWidth: '300px' }}>
                                <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>{payload[0].name}</p>
                                {payload[0].payload.imageUrl && (
                                  <img 
                                    src={payload[0].payload.imageUrl} 
                                    alt="Ad" 
                                    style={{ width: '100%', height: 'auto', maxHeight: '150px', objectFit: 'cover', borderRadius: '4px', marginBottom: '8px' }}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                )}
                                <p style={{ margin: '0 0 5px 0', fontSize: '12px', color: '#666', wordBreak: 'break-all' }}>{payload[0].payload.link}</p>
                                <p style={{ margin: '3px 0', color: payload[0].fill }}>
                                  Impressions: {payload[0].value.toLocaleString()}
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Clicks Distribution Pie Chart */}
                <div className={styles.chartCard}>
                  <h3 className={styles.chartTitle}>🖱️ Engagement Distribution</h3>
                  <p className={styles.chartSubtitle}>Which ads drive the most user interactions</p>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={adsData.topAds.map((ad, idx) => ({
                          name: `Ad ${idx + 1}`,
                          value: ad.clicks,
                          link: ad.link,
                          imageUrl: ad.imageUrl || ad.videoUrl,
                        }))}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {adsData.topAds.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444'][index % 5]} 
                          />
                        ))}
                      </Pie>
                      <Tooltip 
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <div style={{ backgroundColor: 'white', padding: '10px', border: '1px solid #ccc', borderRadius: '4px', maxWidth: '300px' }}>
                                <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>{payload[0].name}</p>
                                {payload[0].payload.imageUrl && (
                                  <img 
                                    src={payload[0].payload.imageUrl} 
                                    alt="Ad" 
                                    style={{ width: '100%', height: 'auto', maxHeight: '150px', objectFit: 'cover', borderRadius: '4px', marginBottom: '8px' }}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                )}
                                <p style={{ margin: '0 0 5px 0', fontSize: '12px', color: '#666', wordBreak: 'break-all' }}>{payload[0].payload.link}</p>
                                <p style={{ margin: '3px 0', color: payload[0].fill }}>
                                  Clicks: {payload[0].value.toLocaleString()}
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Detailed Ads Performance Table */}
              <div className={styles.adsTableSection}>
                <h3 className={styles.subSectionTitle}>🏆 Top Performing Ads Leaderboard</h3>
                <p className={styles.tableDescription}>Ranked by impressions • Click-through rate (CTR) performance analysis</p>
                <div className={styles.analyticsTable}>
                  <div className={styles.tableHeader} style={{
                    display: 'grid',
                    gridTemplateColumns: '60px 100px 180px 140px 120px 100px 120px',
                    gap: '16px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white'
                  }}>
                    <div className={styles.col} style={{ color: 'white' }}>Rank</div>
                    <div className={styles.col} style={{ color: 'white' }}>Preview</div>
                    <div className={styles.col} style={{ color: 'white' }}>Ad Link</div>
                    <div className={styles.col} style={{ color: 'white' }}>Impressions</div>
                    <div className={styles.col} style={{ color: 'white' }}>Clicks</div>
                    <div className={styles.col} style={{ color: 'white' }}>CTR (%)</div>
                    <div className={styles.col} style={{ color: 'white' }}>Status</div>
                  </div>
                  {[...adsData.allAds]
                    .sort((a, b) => (b.impressions || 0) - (a.impressions || 0))
                    .slice(0, 10)
                    .map((ad, idx) => {
                      const ctr = ad.impressions > 0 ? ((ad.clicks / ad.impressions) * 100) : 0;
                      const performance = ctr > 15 ? 'Excellent' : ctr > 8 ? 'Good' : ctr > 3 ? 'Average' : 'Poor';
                      const performanceColor = ctr > 15 ? '#10b981' : ctr > 8 ? '#3b82f6' : ctr > 3 ? '#f59e0b' : '#ef4444';
                      
                      return (
                        <div key={ad.id} className={styles.tableRow} style={{
                          display: 'grid',
                          gridTemplateColumns: '60px 100px 180px 140px 120px 100px 120px',
                          gap: '16px',
                          alignItems: 'center'
                        }}>
                          <div className={styles.col}>
                            <span style={{
                              fontWeight: '600',
                              color: '#6b7280',
                              fontSize: '14px'
                            }}>
                              {idx + 1}
                            </span>
                          </div>
                          <div className={styles.col}>
                            {(ad.imageUrl || ad.videoUrl) ? (
                              <img 
                                src={ad.imageUrl || ad.videoUrl} 
                                alt="Ad preview"
                                style={{
                                  width: '80px',
                                  height: '60px',
                                  objectFit: 'cover',
                                  borderRadius: '6px',
                                  border: '1px solid #e5e7eb'
                                }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            ) : (
                              <div style={{
                                width: '80px',
                                height: '60px',
                                background: '#f3f4f6',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '10px',
                                color: '#9ca3af'
                              }}>
                                No Image
                              </div>
                            )}
                          </div>
                          <div className={styles.col}>
                            <a 
                              href={ad.link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ 
                                color: '#3b82f6',
                                textDecoration: 'none',
                                fontSize: '13px',
                                wordBreak: 'break-all',
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                              title={ad.link}
                            >
                              {ad.link}
                            </a>
                          </div>
                          <div className={styles.col} style={{ fontWeight: '500' }}>{(ad.impressions || 0).toLocaleString()}</div>
                          <div className={styles.col} style={{ fontWeight: '500' }}>{(ad.clicks || 0).toLocaleString()}</div>
                          <div className={styles.col} style={{ fontWeight: '600', color: performanceColor }}>{ctr.toFixed(2)}</div>
                          <div className={styles.col}>
                            <span 
                              className={styles.badge}
                              style={{ backgroundColor: performanceColor }}
                            >
                              {performance}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Detailed Content Analytics */}
      {activeTab && (
        <div className={styles.detailedAnalyticsSection}>
          <button className={styles.backButton} onClick={() => handleTabChange(null)}>
            ← Back to Overview
          </button>

          <h2 className={styles.sectionTitle}>
            {activeTab === "video" ? "Peek" : activeTab === "blog" ? "Article" : activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Analytics
          </h2>

          {contentLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
              <Loader />
            </div>
          ) : (
            <>
              <div className={styles.analyticsTable}>
                <div className={styles.tableHeader} style={{
                  display: 'grid',
                  gridTemplateColumns: activeTab === 'ad' ? '100px 2fr 120px 120px 140px 120px' : '100px 2fr 120px 140px 120px',
                  gap: '16px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                }}>
                  <div className={styles.col} style={{ color: 'white' }}>Preview</div>
                  <div className={styles.col} style={{ color: 'white' }}>{activeTab === "ad" ? "Link" : "Title"}</div>
                  {activeTab === "ad" ? (
                    <>
                      <div className={styles.col} style={{ color: 'white' }}>Impressions</div>
                      <div className={styles.col} style={{ color: 'white' }}>Clicks</div>
                    </>
                  ) : (
                    <div className={styles.col} style={{ color: 'white' }}>Views</div>
                  )}
                  <div className={styles.col} style={{ color: 'white' }}>Created At</div>
                  <div className={styles.col} style={{ color: 'white' }}>Status</div>
                </div>
                {contentAnalytics.length > 0 ? (
                  [...contentAnalytics].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map((item, idx) => {
                    // Get the correct image field based on content type
                    let imageUrl = null;
                    if (activeTab === 'video') {
                      imageUrl = item.thumbnailUrl;
                    } else if (activeTab === 'blog') {
                      imageUrl = item.sharingImage;
                    } else if (activeTab === 'music' || activeTab === 'audio') {
                      imageUrl = item.thumbnailUrl;
                    } else if (activeTab === 'ad') {
                      imageUrl = item.imageUrl || item.videoUrl;
                    }
                    
                    // Fallback to other possible image fields if primary field is empty
                    if (!imageUrl) {
                      imageUrl = item.thumbnail || item.image || item.sharpImages?.[0] || item.imageUrl || item.videoUrl || null;
                    }
                    
                    return (
                    <div key={idx} className={styles.tableRow} style={{
                      display: 'grid',
                      gridTemplateColumns: activeTab === 'ad' ? '100px 2fr 120px 120px 140px 120px' : '100px 2fr 120px 140px 120px',
                      gap: '16px',
                      alignItems: 'center'
                    }}>
                      <div className={styles.col}>
                        {imageUrl ? (
                          <img 
                            src={imageUrl} 
                            alt="Preview"
                            style={{
                              width: '80px',
                              height: '60px',
                              objectFit: 'cover',
                              borderRadius: '6px',
                              border: '1px solid #e5e7eb'
                            }}
                            onError={(e) => { 
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = `<div style="width: 80px; height: 60px; background: #f3f4f6; borderRadius: 6px; display: flex; alignItems: center; justifyContent: center; fontSize: 10px; color: #9ca3af;">No Image</div>`;
                            }}
                          />
                        ) : (
                          <div style={{
                            width: '80px',
                            height: '60px',
                            background: '#f3f4f6',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '10px',
                            color: '#9ca3af'
                          }}>
                            No Image
                          </div>
                        )}
                      </div>
                      <div className={styles.col}>
                        <span
                          style={{
                            wordWrap: "break-word",
                            maxWidth: "100%",
                          }}
                        >
                          {activeTab === "ad" ? (
                            <a href={item.link} target="_blank" rel="noopener noreferrer">
                              {item.link}
                            </a>
                          ) : (
                            item.title
                          )}
                        </span>
                      </div>
                      {activeTab === "ad" ? (
                        <>
                          <div className={styles.col}>{item.impressions || 0}</div>
                          <div className={styles.col}>{item.clicks || 0}</div>
                        </>
                      ) : (
                        <div className={styles.col}>{item.views}</div>
                      )}
                      <div className={styles.col}>
                        {moment(item.createdAt).format("MM/DD/YYYY")}
                      </div>
                      <div className={styles.col}>
                        <span
                          className={styles.badge}
                          style={{
                            backgroundColor: 
                              item.verified === true || item.status === "ACTIVE" 
                                ? "#3eb489" 
                                : item.status === "PENDING" || item.verified === false
                                ? "#FF7F7F"
                                : "#808080",
                          }}
                        >
                          {item.status || (item.verified ? "Verified" : "Pending")}
                        </span>
                      </div>
                    </div>
                    );
                  })
                ) : (
                  <div style={{ padding: "40px", textAlign: "center" }}>
                    <p>No data available</p>
                  </div>
                )}
              </div>

              {pagination.totalPages > 1 && (
                <div className={styles.paginationContainer}>
                  <ReactPaginate
                    previousLabel="Previous"
                    nextLabel="Next"
                    breakLabel="..."
                    pageCount={pagination.totalPages}
                    marginPagesDisplayed={2}
                    pageRangeDisplayed={5}
                    onPageChange={handlePageChange}
                    containerClassName={styles.pagination}
                    activeClassName={styles.active}
                    disabledClassName={styles.disabled}
                  />
                </div>
              )}

              <div className={styles.paginationInfo}>
                <p>
                  Showing {(pagination.page - 1) * pagination.pageSize + 1} to{" "}
                  {Math.min(
                    pagination.page * pagination.pageSize,
                    pagination.totalCount
                  )}{" "}
                  of {pagination.totalCount} items
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
